import React, { useState, useEffect } from 'react';

/**
 * HostFounderPopup
 * S'affiche une seule fois après la connexion d'un nouvel utilisateur.
 * Stocké via localStorage : 'hv_founder_popup_seen'
 *
 * Props :
 *   user       — objet user Supabase (doit être défini = connecté)
 *   onNavigate — fonction de navigation de l'app
 *   onClose    — callback optionnel à la fermeture
 */
export default function HostFounderPopup({ user, onNavigate, onClose }) {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!user) return;
    const key = `hv_founder_popup_seen_${user.id}`;
    const seen = localStorage.getItem(key);
    if (!seen) {
      // Petit délai pour laisser la page se charger
      const t = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(t);
    }
  }, [user]);

  const handleClose = () => {
    if (!user) return;
    setClosing(true);
    localStorage.setItem(`hv_founder_popup_seen_${user.id}`, '1');
    setTimeout(() => {
      setVisible(false);
      setClosing(false);
      onClose?.();
    }, 250);
  };

  const handleCTA = () => {
    handleClose();
    onNavigate('newlisting');
  };

  if (!visible) return null;

  const isMobile = window.innerWidth < 768;

  return (
    <>
      <style>{`
        @keyframes hv-fade-in  { from { opacity:0 } to { opacity:1 } }
        @keyframes hv-slide-up { from { opacity:0; transform:translateY(32px) scale(.97) } to { opacity:1; transform:translateY(0) scale(1) } }
        @keyframes hv-fade-out { from { opacity:1 } to { opacity:0 } }
        .hv-overlay { animation: ${closing ? 'hv-fade-out' : 'hv-fade-in'} .25s ease both }
        .hv-modal   { animation: ${closing ? 'hv-fade-out' : 'hv-slide-up'} .35s cubic-bezier(.22,1,.36,1) both }
        .hv-cta-btn:hover { transform:translateY(-2px); box-shadow:0 8px 28px rgba(29,158,117,.35) }
        .hv-cta-btn { transition: all .2s ease }
        .hv-skip:hover { color: #555 }
        .hv-skip { transition: color .15s ease }
      `}</style>

      {/* Overlay */}
      <div
        className="hv-overlay"
        onClick={handleClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: 'rgba(10,15,12,.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: isMobile ? 'flex-end' : 'center',
          justifyContent: 'center',
          padding: isMobile ? 0 : '1.5rem',
        }}
      >
        {/* Modal */}
        <div
          className="hv-modal"
          onClick={e => e.stopPropagation()}
          style={{
            background: '#fff',
            borderRadius: isMobile ? '24px 24px 0 0' : 24,
            width: '100%',
            maxWidth: isMobile ? '100%' : 480,
            overflow: 'hidden',
            boxShadow: '0 32px 80px rgba(0,0,0,.22)',
          }}
        >
          {/* Image villa */}
          <div style={{
            position: 'relative',
            height: isMobile ? 200 : 220,
            background: 'linear-gradient(135deg, #0D2A1A 0%, #1A4A2E 100%)',
            overflow: 'hidden',
          }}>
            <img
              src="https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=900&q=80&fit=crop"
              alt="Villa"
              style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: .85 }}
              onError={e => { e.target.style.display = 'none'; }}
            />
            {/* Overlay dégradé bas */}
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, height: 80,
              background: 'linear-gradient(to top, rgba(0,0,0,.5), transparent)',
            }} />
            {/* Badge fondateur */}
            <div style={{
              position: 'absolute', top: 16, left: 16,
              background: 'rgba(29,158,117,.9)',
              backdropFilter: 'blur(6px)',
              borderRadius: 20, padding: '5px 14px',
              fontSize: 12, fontWeight: 600, color: '#fff',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: '50%', background: '#fff',
                display: 'inline-block',
              }} />
              Offre fondateurs · Places limitées
            </div>
            {/* Bouton fermer */}
            <button
              onClick={handleClose}
              style={{
                position: 'absolute', top: 14, right: 14,
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(255,255,255,.2)',
                border: '1px solid rgba(255,255,255,.3)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, color: '#fff', backdropFilter: 'blur(4px)',
              }}
            >×</button>
          </div>

          {/* Contenu */}
          <div style={{ padding: isMobile ? '24px 20px 32px' : '28px 28px 32px' }}>

            {/* Titre */}
            <h2 style={{
              fontFamily: 'var(--serif, Georgia, serif)',
              fontSize: isMobile ? 22 : 24,
              letterSpacing: '-0.5px',
              lineHeight: 1.25,
              color: '#1A1A18',
              marginBottom: 12,
            }}>
              Devenez hôte fondateur<br />
              <em style={{ color: '#1D9E75', fontStyle: 'italic', fontWeight: 400 }}>et gardez plus de revenus.</em>
            </h2>

            {/* Description */}
            <p style={{
              fontSize: 14, color: '#666', lineHeight: 1.7, marginBottom: 20,
            }}>
              Rejoignez Havenly maintenant et bénéficiez d'une commission de{' '}
              <strong style={{ color: '#1D9E75' }}>8% pendant 3 mois</strong>{' '}
              au lieu de 12%. C'est toujours moins qu'Airbnb, Booking et tous les autres.
            </p>

            {/* Comparatif */}
            <div style={{
              background: '#F7F7F5',
              borderRadius: 14, padding: '14px 16px',
              display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
              gap: 10, marginBottom: 24,
            }}>
              {[
                { label: 'Airbnb',   val: '~15%',  muted: true },
                { label: 'Booking',  val: '~17%',  muted: true },
                { label: 'Havenly ✓', val: '8%',   muted: false },
              ].map(c => (
                <div key={c.label} style={{
                  textAlign: 'center',
                  background: c.muted ? 'transparent' : '#E8F8F2',
                  borderRadius: 10, padding: '10px 8px',
                  border: c.muted ? 'none' : '1px solid #B8E8D5',
                }}>
                  <div style={{
                    fontSize: c.muted ? 18 : 22,
                    fontWeight: 700,
                    color: c.muted ? '#AAA' : '#1D9E75',
                    fontFamily: 'var(--serif, Georgia, serif)',
                    textDecoration: c.muted ? 'line-through' : 'none',
                    marginBottom: 4,
                  }}>{c.val}</div>
                  <div style={{ fontSize: 11, color: c.muted ? '#BBB' : '#0F6E56', fontWeight: 500 }}>
                    {c.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Avantages */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
              {[
                '📅 Importez votre calendrier Airbnb en 30 secondes',
                '🤖 Emma IA répond à vos voyageurs 24h/24',
                '💸 Payé 24h après chaque check-in',
                '🇫🇷 Plateforme 100% française',
              ].map(a => (
                <div key={a} style={{ fontSize: 13, color: '#555', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {a}
                </div>
              ))}
            </div>

            {/* CTA principal */}
            <button
              className="hv-cta-btn"
              onClick={handleCTA}
              style={{
                width: '100%', padding: '15px',
                background: '#1D9E75', color: '#fff',
                border: 'none', borderRadius: 14,
                fontSize: 16, fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'var(--sans, sans-serif)',
                marginBottom: 12,
              }}
            >
              Devenir hôte fondateur →
            </button>

            {/* Lien skip */}
            <button
              className="hv-skip"
              onClick={handleClose}
              style={{
                width: '100%', padding: '8px',
                background: 'none', border: 'none',
                fontSize: 13, color: '#AAA',
                cursor: 'pointer',
                fontFamily: 'var(--sans, sans-serif)',
              }}
            >
              Continuer en tant que voyageur
            </button>
          </div>
        </div>
      </div>
    </>
  );
}